/* ============================================================
   KPS PDP - shared behaviour for the kps-pdp-* sections.
   Loaded once via snippets/kps-pdp-head.liquid.

   Design rules this file follows:
   - Every initialiser is IDEMPOTENT and stamps its root with a data flag, so
     re-running on shopify:section:load never double-binds.
   - Nothing here is required for the page to work. Accordions are <details>,
     modals are <dialog>, the before/after handle is an <input type=range>, and
     the buy form posts a real variant id. JS only enhances.
   - No global state, no theme internals touched. The one theme integration is
     opening the cart drawer, and it is feature-detected.
   - All motion is gated on prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function prefersReduced() {
    return !!(REDUCED && REDUCED.matches);
  }

  /* Data-saver / metered connection - used to suppress video autoplay. */
  function saveData() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return !!(c && (c.saveData === true || /^(slow-2g|2g)$/.test(c.effectiveType || '')));
  }

  function once(el, key) {
    if (!el || el.dataset[key] === '1') return false;
    el.dataset[key] = '1';
    return true;
  }

  function each(root, selector, fn) {
    Array.prototype.forEach.call(root.querySelectorAll(selector), fn);
  }

  /* --------------------------------------------------------
     Header height -> --kps-pdp-header-h
     The sticky info column and every anchor offset read this. Measured from
     whatever element actually behaves as the sticky header, so it stays
     correct when the announcement bar is toggled.
     -------------------------------------------------------- */
  var headerEl = null;

  function measureHeader() {
    if (!headerEl) {
      headerEl =
        document.querySelector('.shopify-section-header, #shopify-section-header, .site-header, header[role="banner"]') ||
        document.querySelector('header');
    }
    if (!headerEl) return;
    var h = Math.round(headerEl.getBoundingClientRect().height);
    if (h > 0 && h < 400) {
      document.documentElement.style.setProperty('--kps-pdp-header-h', h + 'px');
    }
  }

  var resizeTimer = null;
  function onResize() {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      measureHeader();
      /* The sticky column, whichever one that is - not always the info
         column. Re-measured here because the viewport height is half of the
         too-tall test. */
      each(document, '[data-kps-pdp-sticky]', checkStickyHeight);
      each(document, '[data-kps-pdp-rail]', function (r) { updateRailNav(r); });
    }, 150);
  }

  /* --------------------------------------------------------
     Scroll reveal
     One observer for the whole page. Elements are unobserved once revealed.
     -------------------------------------------------------- */
  var revealObserver = null;

  function initReveal(root) {
    if (prefersReduced() || !('IntersectionObserver' in window)) {
      each(root, '.kps-pdp-reveal', function (el) { el.classList.add('is-in'); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );
    }
    each(root, '.kps-pdp-reveal', function (el) {
      if (!once(el, 'kpsReveal')) return;
      revealObserver.observe(el);
    });
  }

  /* --------------------------------------------------------
     Smooth in-page anchors with header offset
     -------------------------------------------------------- */
  function initAnchors(root) {
    each(root, 'a[href^="#"]', function (link) {
      if (!once(link, 'kpsAnchor')) return;
      link.addEventListener('click', function (e) {
        var id = link.getAttribute('href');
        if (!id || id === '#' || id.length < 2) return;
        var target;
        try {
          target = document.querySelector(id);
        } catch (err) {
          return;
        }
        if (!target) return;
        e.preventDefault();
        var offset =
          parseInt(getComputedStyle(document.documentElement).getPropertyValue('--kps-pdp-header-h'), 10) || 64;
        var top = target.getBoundingClientRect().top + window.pageYOffset - offset - 16;
        window.scrollTo({ top: top, behavior: prefersReduced() ? 'auto' : 'smooth' });
        /* Move focus so keyboard users land where the page scrolled to. */
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    });
  }

  /* --------------------------------------------------------
     Rails - arrows and dots for any [data-kps-pdp-rail]
     Scroll position drives the UI, so a swipe and an arrow click stay in sync.
     -------------------------------------------------------- */
  function railItems(rail) {
    return Array.prototype.filter.call(rail.children, function (el) {
      return el.nodeType === 1;
    });
  }

  /* A centred rail (the UGC wall) puts its current item in the middle of the
     viewport rather than against the left edge, so both the lookup and the
     scroll target measure item centres instead of item starts. */
  function railIsCentered(rail) {
    return rail.hasAttribute('data-kps-pdp-rail-center');
  }

  /* The rail's own scroll-padding at the inline start.

     A rail that bleeds to the screen edge re-pads itself and insets its
     snapport by the same amount, so the snap position for an item sits that
     much SHORT of the item's offset. Measured rather than assumed, because the
     rails that zero their padding (before/after, the routine rail, --persist
     from 990px) zero their scroll-padding with it. */
  function railStartPad(rail) {
    var cs = window.getComputedStyle(rail);
    var v = parseFloat(cs.scrollPaddingInlineStart);
    if (!isFinite(v)) v = parseFloat(cs.scrollPaddingLeft);
    return isFinite(v) ? v : 0;
  }

  /* Which item the rail rests on at a given scroll offset. Split out of
     railIndex so the same measurement can be taken at an offset the rail is
     not currently at - which is how the reachable range below is found. */
  function railIndexAt(rail, scrollLeft) {
    var items = railItems(rail);
    if (!items.length) return 0;
    var centered = railIsCentered(rail);
    var anchor = scrollLeft + (centered ? rail.clientWidth / 2 : railStartPad(rail));
    var best = 0;
    var bestDelta = Infinity;
    for (var i = 0; i < items.length; i++) {
      var pos = items[i].offsetLeft - rail.offsetLeft + (centered ? items[i].offsetWidth / 2 : 0);
      var delta = Math.abs(pos - anchor);
      if (delta < bestDelta) {
        bestDelta = delta;
        best = i;
      }
    }
    return best;
  }

  function railIndex(rail) {
    return railIndexAt(rail, rail.scrollLeft);
  }

  /* The highest index the rail can actually come to rest on.

     A rail stops scrolling when its right edge meets the end of its content.
     At that point the last cards are all on screen but none of them is at the
     START of the rail any more, so a rail showing 3.8 of 6 cards has THREE
     resting positions, not six: 3, 4 and 5 cannot be reached, and a dot
     offering one of them can never light up however hard it is pressed.

     A centred rail is exempt. The UGC wall pads both ends by half a screen
     precisely so that every tile, first and last included, can reach the
     middle. */
  function railMaxIndex(rail) {
    var items = railItems(rail);
    if (!items.length) return 0;
    if (railIsCentered(rail)) return items.length - 1;
    var max = rail.scrollWidth - rail.clientWidth;
    if (max <= 2) return 0;
    return railIndexAt(rail, max);
  }

  function railScrollTo(rail, index, instant) {
    var items = railItems(rail);
    if (!items.length) return;
    index = Math.max(0, Math.min(items.length - 1, index));
    var left = items[index].offsetLeft - rail.offsetLeft;
    if (railIsCentered(rail)) {
      left -= (rail.clientWidth - items[index].offsetWidth) / 2;
    } else {
      /* Land ON the snap position instead of a padding's width past it.
         Overshooting and letting mandatory snapping drag the rail back reads
         as a stutter at the end of every arrow and dot press. */
      left -= railStartPad(rail);
    }
    rail.scrollTo({
      left: left,
      behavior: instant || prefersReduced() ? 'auto' : 'smooth'
    });
  }

  /* The item a dot stands for. That is the dot's own index, unless the rail
     holds fewer dots than items because it repeats its set to loop (the UGC
     wall) - then it is the copy of that item nearest the current position, so
     picking a dot never scrolls the length of the clone band. */
  function railDotTarget(rail, group, dot) {
    var items = railItems(rail);
    var count = group ? group.querySelectorAll('[data-kps-pdp-rail-dot]').length : 0;
    /* One dot per item: clamped to the reachable range, so a dot can never ask
       for a position the rail will refuse and then light a different one. */
    if (!count || count >= items.length) return Math.min(dot, railMaxIndex(rail));
    var at = dot + count * Math.round((railIndex(rail) - dot) / count);
    return Math.max(0, Math.min(items.length - 1, at));
  }

  function updateRailNav(rail) {
    var group = rail.closest('[data-kps-pdp-rail-group]') || rail.parentNode;
    if (!group) return;

    /* A rail that has become a grid (desktop) has no overflow to scroll. */
    var scrollable = rail.scrollWidth - rail.clientWidth > 2;
    var index = railIndex(rail);

    var prev = group.querySelector('[data-kps-pdp-rail-prev]');
    var next = group.querySelector('[data-kps-pdp-rail-next]');
    if (prev) prev.disabled = !scrollable || rail.scrollLeft <= 2;
    if (next) next.disabled = !scrollable || rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;

    /* ---- Dots ----
       Two kinds of rail arrive here.

       A rail that repeats its set in order to loop holds more items than it has
       dots (the UGC wall), so the dot for an item is its index modulo the dot
       count and every dot is always in play.

       Every other rail renders one dot per item in Liquid - before it can know
       how many items will fit on screen, because that is a breakpoint away.
       Once more items exist than fit, the dots past railMaxIndex() stand for a
       resting position the rail can never reach, so they are stood down rather
       than left on screen as dead targets that light up a different dot when
       pressed. The count follows the breakpoint and is re-evaluated on every
       resize, which is also what the reference's Swiper pagination does. */
    var dots = group.querySelectorAll('[data-kps-pdp-rail-dot]');
    var period = dots.length && dots.length < railItems(rail).length ? dots.length : 0;
    var lastDot = period ? dots.length - 1 : railMaxIndex(rail);
    var atDot = period ? index % period : Math.min(index, lastDot);
    Array.prototype.forEach.call(dots, function (dot) {
      var di = parseInt(dot.getAttribute('data-kps-pdp-rail-dot'), 10) || 0;
      /* Nothing to page through at all - the arrows are already disabled, and a
         row of inert dots reads as a broken control rather than as a position. */
      var usable = scrollable && di <= lastDot;
      if (usable) dot.removeAttribute('hidden');
      else dot.setAttribute('hidden', '');
      var on = usable && di === atDot;
      dot.classList.toggle('is-active', on);
      dot.setAttribute('aria-selected', on ? 'true' : 'false');
      dot.setAttribute('tabindex', on ? '0' : '-1');
    });

    /* Opt-in: the card at the current position carries .is-active, so a rail
       can highlight it (key ingredients). Liquid puts the class on the first
       card, so the highlight is already right before this ever runs. */
    if (rail.hasAttribute('data-kps-pdp-rail-active')) {
      var cards = railItems(rail);
      for (var c = 0; c < cards.length; c++) {
        cards[c].classList.toggle('is-active', c === index);
      }
    }

    /* One event per real change, so a section can react to its current item
       without adding a second scroll listener of its own. */
    if (rail.kpsRailIndex !== index) {
      rail.kpsRailIndex = index;
      rail.dispatchEvent(new CustomEvent('kps:rail:change', { detail: { index: index } }));
    }
  }

  function initRail(rail) {
    if (!once(rail, 'kpsRail')) return;
    var group = rail.closest('[data-kps-pdp-rail-group]') || rail.parentNode;

    var scrollTimer = null;
    rail.addEventListener(
      'scroll',
      function () {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(function () { updateRailNav(rail); }, 60);
      },
      { passive: true }
    );

    if (group) {
      var prev = group.querySelector('[data-kps-pdp-rail-prev]');
      var next = group.querySelector('[data-kps-pdp-rail-next]');
      if (prev) prev.addEventListener('click', function () { railScrollTo(rail, railIndex(rail) - 1); });
      /* Clamped to the last reachable position, so the final press moves the
         rail to its end rather than asking for a card it cannot bring to the
         start and landing somewhere the dots then disagree with. */
      if (next) {
        next.addEventListener('click', function () {
          railScrollTo(rail, Math.min(railIndex(rail) + 1, railMaxIndex(rail)));
        });
      }

      each(group, '[data-kps-pdp-rail-dot]', function (dot) {
        dot.addEventListener('click', function () {
          var at = parseInt(dot.getAttribute('data-kps-pdp-rail-dot'), 10) || 0;
          railScrollTo(rail, railDotTarget(rail, group, at));
        });
      });

      /* Roving tabindex needs arrow keys to go with it, or the dots are a
         tablist a keyboard can enter but not move around in. Dots that have
         been stood down are skipped, so the keys never land on a position the
         rail cannot reach. */
      each(group, '[data-kps-pdp-rail-dots]', function (wrap) {
        wrap.addEventListener('keydown', function (e) {
          var from = e.target.closest && e.target.closest('[data-kps-pdp-rail-dot]');
          if (!from) return;
          var live = Array.prototype.filter.call(
            wrap.querySelectorAll('[data-kps-pdp-rail-dot]'),
            function (d) { return !d.hasAttribute('hidden'); }
          );
          var i = live.indexOf(from);
          if (i < 0 || !live.length) return;
          var to = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') to = live[(i + 1) % live.length];
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') to = live[(i - 1 + live.length) % live.length];
          else if (e.key === 'Home') to = live[0];
          else if (e.key === 'End') to = live[live.length - 1];
          if (!to) return;
          e.preventDefault();
          to.focus();
          to.click();
        });
      });

      /* Autoplay, opt-in per section and never under reduced motion. */
      var delay = parseInt(group.getAttribute('data-autoplay') || '0', 10);
      if (delay > 0 && !prefersReduced()) {
        var timer = window.setInterval(function () {
          /* Wraps at the last REACHABLE position. Wrapping at the last item
             instead would leave a rail whose tail cards share one resting
             position sitting at its end, ticking against a wall. */
          var last = railMaxIndex(rail);
          var i = railIndex(rail);
          railScrollTo(rail, i >= last ? 0 : i + 1);
        }, delay);
        /* Stop for good on any real interaction - fighting the visitor for
           control of a carousel is worse than losing the animation. */
        ['pointerdown', 'keydown', 'focusin'].forEach(function (ev) {
          group.addEventListener(ev, function () { window.clearInterval(timer); }, { once: true });
        });
      }
    }

    updateRailNav(rail);
  }

  /* --------------------------------------------------------
     Testimonial mini-slider
     -------------------------------------------------------- */
  function initTestimonials(root) {
    each(root, '[data-kps-pdp-ts]', function (el) {
      if (!once(el, 'kpsTs')) return;

      var slides = Array.prototype.slice.call(el.querySelectorAll('[data-kps-pdp-ts-slide]'));
      var dots = Array.prototype.slice.call(el.querySelectorAll('[data-kps-pdp-ts-dot]'));
      if (slides.length < 2) return;

      var interval = parseInt(el.getAttribute('data-interval'), 10) || 6000;
      var current = 0;
      var timer = null;

      function show(i) {
        current = (i + slides.length) % slides.length;
        slides.forEach(function (s, si) {
          var on = si === current;
          s.classList.toggle('is-active', on);
          if (on) s.removeAttribute('aria-hidden');
          else s.setAttribute('aria-hidden', 'true');
        });
        dots.forEach(function (d, di) {
          var on = di === current;
          d.classList.toggle('is-active', on);
          d.setAttribute('aria-selected', on ? 'true' : 'false');
          d.setAttribute('tabindex', on ? '0' : '-1');
        });
      }

      function start() {
        if (prefersReduced() || timer) return;
        timer = window.setInterval(function () { show(current + 1); }, interval);
      }
      function stop() {
        window.clearInterval(timer);
        timer = null;
      }

      dots.forEach(function (d) {
        d.addEventListener('click', function () {
          stop();
          show(parseInt(d.getAttribute('data-kps-pdp-ts-dot'), 10) || 0);
        });
      });

      /* The dot row is a role="tablist" with a roving tabindex, so it needs
         arrow keys to be operable at all once focus is inside it - same
         contract as the rail dots. */
      if (dots.length > 1) {
        var dotWrap = dots[0].parentNode;
        if (dotWrap) {
          dotWrap.addEventListener('keydown', function (e) {
            var from = e.target.closest && e.target.closest('[data-kps-pdp-ts-dot]');
            if (!from) return;
            var i = dots.indexOf(from);
            if (i < 0) return;
            var to = -1;
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') to = (i + 1) % dots.length;
            else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') to = (i - 1 + dots.length) % dots.length;
            else if (e.key === 'Home') to = 0;
            else if (e.key === 'End') to = dots.length - 1;
            if (to < 0) return;
            e.preventDefault();
            stop();
            show(to);
            dots[to].focus();
          });
        }
      }

      el.addEventListener('mouseenter', stop);
      el.addEventListener('mouseleave', start);
      el.addEventListener('focusin', stop);
      el.addEventListener('focusout', start);

      /* Do not animate a slider nobody can see. */
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (e) { e.isIntersecting ? start() : stop(); });
        }).observe(el);
      } else {
        start();
      }

      show(0);
    });
  }

  /* --------------------------------------------------------
     Accordion groups - optional single-open behaviour
     -------------------------------------------------------- */
  function initAccordions(root) {
    each(root, '[data-kps-pdp-acc], [data-kps-pdp-faq]', function (group) {
      if (!once(group, 'kpsAcc')) return;
      if (group.getAttribute('data-allow-multiple') === 'true') return;

      each(group, 'details', function (d) {
        d.addEventListener('toggle', function () {
          if (!d.open) return;
          each(group, 'details[open]', function (other) {
            if (other !== d) other.open = false;
          });
        });
      });
    });
  }

  /* --------------------------------------------------------
     FAQ tabs
     -------------------------------------------------------- */
  function initFaqTabs(root) {
    each(root, '[data-kps-pdp-faq]', function (shell) {
      if (!once(shell, 'kpsFaqTabs')) return;

      var tabs = Array.prototype.slice.call(shell.querySelectorAll('[data-kps-pdp-tab]'));
      var panels = Array.prototype.slice.call(shell.querySelectorAll('[data-kps-pdp-panel]'));
      if (!tabs.length) return;

      function activate(index) {
        var key = String(index);
        tabs.forEach(function (t) {
          var on = t.getAttribute('data-kps-pdp-tab') === key;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
        });
        panels.forEach(function (p) {
          if (p.getAttribute('data-kps-pdp-panel') === key) p.removeAttribute('hidden');
          else p.setAttribute('hidden', '');
        });
      }

      tabs.forEach(function (t) {
        t.addEventListener('click', function () { activate(t.getAttribute('data-kps-pdp-tab')); });
      });

      shell.addEventListener('keydown', function (e) {
        var t = e.target.closest && e.target.closest('[data-kps-pdp-tab]');
        if (!t) return;
        var i = tabs.indexOf(t);
        if (i < 0) return;
        var next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (e.key === 'Home') next = tabs[0];
        else if (e.key === 'End') next = tabs[tabs.length - 1];
        if (!next) return;
        e.preventDefault();
        activate(next.getAttribute('data-kps-pdp-tab'));
        next.focus();
      });
    });
  }

  /* --------------------------------------------------------
     Count-up
     -------------------------------------------------------- */
  function initCountUp(root) {
    if (prefersReduced() || !('IntersectionObserver' in window)) return;

    each(root, '[data-kps-pdp-countup]', function (el) {
      if (!once(el, 'kpsCount')) return;

      var target = parseFloat(el.getAttribute('data-target'));
      var decimals = parseInt(el.getAttribute('data-decimals'), 10) || 0;
      /* A non-numeric value parses to 0 or NaN; leave the authored text alone
         rather than counting to a number that was never there. */
      if (!isFinite(target) || target <= 0) return;

      var final = el.textContent;

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            io.unobserve(el);

            var duration = 1200;
            var start = null;
            function step(ts) {
              if (start === null) start = ts;
              var t = Math.min(1, (ts - start) / duration);
              /* ease-out cubic */
              var eased = 1 - Math.pow(1 - t, 3);
              el.textContent = (target * eased).toFixed(decimals);
              if (t < 1) window.requestAnimationFrame(step);
              else el.textContent = final;
            }
            el.textContent = (0).toFixed(decimals);
            window.requestAnimationFrame(step);
          });
        },
        { threshold: 0.4 }
      );
      io.observe(el);
    });
  }

  /* --------------------------------------------------------
     Before / after
     The range input owns the value; this mirrors it into a CSS variable and
     drags it from the frame on touch.

     WHY THE FRAME HAS TO DRAG IT ON TOUCH
     A native range is the right control - keyboard, role, aria-valuenow for
     free - but it is not draggable with a finger here:

       - iOS Safari only moves a range thumb when the touch STARTS on the
         thumb, and this thumb is a transparent 45px circle. A swipe across
         the photograph landed on the track and did nothing at all.
       - Below 768px the frame rides in a horizontal scroll-snap rail, so the
         swipe that did reach the browser was read as a carousel scroll.

     So wherever a coarse pointer exists the section's CSS takes the input out
     of hit-testing ([data-ba-drag], set below so this stays an enhancement)
     and the divider follows the finger's x inside the frame instead. The
     input is still the value's owner and still fully keyboard operable -
     pointer-events changes neither tab order nor focus nor arrow keys.

     TOUCH EVENTS, NOT POINTER EVENTS
     Pointer events are the tidier API and they do not survive iOS here. Inside
     a scrollable ancestor - and below 768px this frame lives in one - Safari
     cancels the pointer stream the moment it suspects the gesture is a scroll,
     and setPointerCapture does not reliably hold a touch through that. Touch
     events have none of those problems: they are implicitly captured by the
     element the gesture STARTED on, and preventDefault() on a non-passive
     touchmove is what actually stops the rail scrolling underneath. The mouse
     keeps the pointer path below; the finger gets its own.

     A touch drag only starts once the finger has travelled HORIZONTALLY, so a
     page scroll that happens to begin on a photograph leaves the divider
     alone: the frame's touch-action: pan-y keeps the vertical gesture with
     the browser and the horizontal one with us.
     -------------------------------------------------------- */
  function initBeforeAfter(root) {
    each(root, '[data-kps-pdp-ba]', function (el) {
      if (!once(el, 'kpsBa')) return;
      var range = el.querySelector('[data-kps-pdp-ba-range]');
      if (!range) return;
      var after = el.querySelector('[data-kps-pdp-ba-after]');

      function sync() {
        var pos = range.value + '%';
        el.style.setProperty('--kps-pdp-ba-pos', pos);
        /* The CSS clips the AFTER layer with that variable, and Safari does
           not always repaint a clip-path when an inherited custom property
           behind it changes - the divider moved, the photograph did not. So
           the same value is written straight onto the layer, where it is an
           ordinary style change that cannot be missed. The rule stays as the
           pre-script state. */
        if (after) {
          var clip = 'inset(0 0 0 ' + pos + ')';
          after.style.webkitClipPath = clip;
          after.style.clipPath = clip;
        }
      }
      range.addEventListener('input', sync);
      range.addEventListener('change', sync);
      sync();

      el.setAttribute('data-ba-drag', '');

      /* Horizontal travel, in px, before a gesture counts as a drag. */
      var THRESHOLD = 4;
      var startX = 0;
      var startY = 0;

      function setFrom(clientX) {
        var box = el.getBoundingClientRect();
        if (!box.width) return;
        var pct = Math.round(((clientX - box.left) / box.width) * 100);
        pct = Math.max(0, Math.min(100, pct));
        if (String(pct) === String(range.value)) return;
        range.value = pct;
        /* The range stays the single source of truth, so the mirror above is
           what paints - no second path to the divider. */
        range.dispatchEvent(new Event('input', { bubbles: true }));
      }

      /* ---- Finger ---- */
      var touchId = null;
      var touchDragging = false;

      function touchById(list, id) {
        for (var i = 0; i < list.length; i++) {
          if (list[i].identifier === id) return list[i];
        }
        return null;
      }

      /* Bound on the frame, so a touch that lands on the input (a browser that
         did not apply the pointer-events rule) is still caught on the way up
         and still has its default - the input's own dragging - prevented. */
      el.addEventListener(
        'touchstart',
        function (e) {
          if (touchId !== null) return;
          var t = e.changedTouches[0];
          if (!t) return;
          touchId = t.identifier;
          touchDragging = false;
          startX = t.clientX;
          startY = t.clientY;
        },
        { passive: true }
      );

      el.addEventListener(
        'touchmove',
        function (e) {
          if (touchId === null) return;
          var t = touchById(e.changedTouches, touchId) || touchById(e.touches, touchId);
          if (!t) return;
          if (!touchDragging) {
            var dx = t.clientX - startX;
            var dy = t.clientY - startY;
            /* Still ambiguous, or plainly a scroll: leave it to the browser. */
            if (Math.abs(dx) < THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;
            touchDragging = true;
          }
          /* Non-passive on purpose: this is the call that stops the rail
             scrolling sideways under the finger. */
          if (e.cancelable) e.preventDefault();
          setFrom(t.clientX);
        },
        { passive: false }
      );

      function endTouch(e) {
        if (touchId === null || !touchById(e.changedTouches, touchId)) return;
        touchId = null;
        if (!touchDragging) return;
        touchDragging = false;
        range.dispatchEvent(new Event('change', { bubbles: true }));
      }
      el.addEventListener('touchend', endTouch);
      el.addEventListener('touchcancel', endTouch);

      /* ---- Mouse ----
         Only where the input is inert (a touch-screen laptop matches the CSS
         media query too). Everywhere else the target IS the input and its own
         click-to-jump, drag and focus are left alone. */
      if (!window.PointerEvent) return;
      var mouseDragging = false;

      el.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.target === range || !e.isPrimary) return;
        mouseDragging = true;
        try {
          el.setPointerCapture(e.pointerId);
        } catch (err) {
          /* Without capture the drag simply stops at the frame's edge. */
        }
        /* A mouse press can never become a scroll, so it jumps at once. */
        setFrom(e.clientX);
        range.focus({ preventScroll: true });
        e.preventDefault();
      });

      el.addEventListener('pointermove', function (e) {
        if (!mouseDragging || e.pointerType !== 'mouse') return;
        if (e.cancelable) e.preventDefault();
        setFrom(e.clientX);
      });

      function endMouse(e) {
        if (!mouseDragging) return;
        mouseDragging = false;
        if (el.hasPointerCapture && el.hasPointerCapture(e.pointerId)) {
          el.releasePointerCapture(e.pointerId);
        }
        range.dispatchEvent(new Event('change', { bubbles: true }));
      }
      el.addEventListener('pointerup', endMouse);
      el.addEventListener('pointercancel', endMouse);
    });
  }

  /* --------------------------------------------------------
     Synced list <-> image (sensitive-skin section)
     -------------------------------------------------------- */
  function initSync(root) {
    each(root, '[data-kps-pdp-sync]', function (el) {
      if (!once(el, 'kpsSync')) return;

      var items = Array.prototype.slice.call(el.querySelectorAll('[data-kps-pdp-sync-item]'));
      var images = Array.prototype.slice.call(el.querySelectorAll('[data-kps-pdp-sync-image]'));
      if (items.length < 2) return;

      var current = 0;
      var timer = null;

      function show(i) {
        current = (i + items.length) % items.length;
        items.forEach(function (it, ii) {
          var on = ii === current;
          it.classList.toggle('is-active', on);
          it.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        images.forEach(function (img, ii) {
          var on = ii === current;
          img.classList.toggle('is-active', on);
          if (on) img.removeAttribute('aria-hidden');
          else img.setAttribute('aria-hidden', 'true');
        });
      }

      function stop() {
        window.clearInterval(timer);
        timer = null;
      }

      items.forEach(function (it, ii) {
        /* Tap and keyboard activation. */
        it.addEventListener('click', function () { stop(); show(ii); });
        /* Hover only where hover is real - a touch device must not get a
           sticky hover state (spec 1.5.8). */
        it.addEventListener('pointerenter', function (e) {
          if (e.pointerType !== 'mouse') return;
          stop();
          show(ii);
        });
        /* Keyboard focus also selects, so the image is never hover-only info. */
        it.addEventListener('focus', function () { stop(); show(ii); });
      });

      var delay = parseInt(el.getAttribute('data-auto-advance') || '0', 10);
      if (delay > 0 && !prefersReduced()) {
        timer = window.setInterval(function () { show(current + 1); }, delay);
        ['pointerdown', 'keydown', 'focusin'].forEach(function (ev) {
          el.addEventListener(ev, stop, { once: true });
        });
      }

      show(0);
    });
  }

  /* --------------------------------------------------------
     Product gallery
     -------------------------------------------------------- */
  function initGallery(root) {
    each(root, '[data-kps-pdp-gallery]', function (gallery) {
      if (!once(gallery, 'kpsGallery')) return;

      var track = gallery.querySelector('[data-kps-pdp-gallery-track]');
      var slides = Array.prototype.slice.call(gallery.querySelectorAll('[data-kps-pdp-gallery-slide]'));
      var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-kps-pdp-thumb]'));
      var dots = Array.prototype.slice.call(gallery.querySelectorAll('[data-kps-pdp-gallery-dots] > *'));
      if (slides.length < 2) return;

      var current = 0;

      function paint(i) {
        current = Math.max(0, Math.min(slides.length - 1, i));
        slides.forEach(function (s, si) {
          var on = si === current;
          s.classList.toggle('is-active', on);
          if (on) s.removeAttribute('aria-hidden');
          else s.setAttribute('aria-hidden', 'true');
        });
        thumbs.forEach(function (t, ti) {
          var on = ti === current;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
        });
        dots.forEach(function (d, di) { d.classList.toggle('is-active', di === current); });
      }

      function goTo(i, scroll) {
        paint(i);
        /* Below 990px the stage is a real scroll carousel, so a thumb click
           must move the scroll position, not just toggle classes. */
        if (scroll && track && track.scrollWidth - track.clientWidth > 2) {
          track.scrollTo({
            left: slides[current].offsetLeft - track.offsetLeft,
            behavior: prefersReduced() ? 'auto' : 'smooth'
          });
        }
      }

      thumbs.forEach(function (t, ti) {
        t.addEventListener('click', function () { goTo(ti, true); });
      });

      /* Roving tabindex: one tab stop for the rail, arrows move within it. */
      var thumbWrap = gallery.querySelector('[data-kps-pdp-gallery-thumbs]');
      if (thumbWrap) {
        thumbWrap.addEventListener('keydown', function (e) {
          var i = thumbs.indexOf(e.target.closest('[data-kps-pdp-thumb]'));
          if (i < 0) return;
          var next = -1;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % thumbs.length;
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + thumbs.length) % thumbs.length;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = thumbs.length - 1;
          if (next < 0) return;
          e.preventDefault();
          goTo(next, true);
          thumbs[next].focus();
        });
      }

      /* Swiping the stage updates the thumbs and dots. */
      if (track) {
        var st = null;
        track.addEventListener(
          'scroll',
          function () {
            window.clearTimeout(st);
            st = window.setTimeout(function () {
              if (track.scrollWidth - track.clientWidth <= 2) return;
              var best = 0;
              var bestDelta = Infinity;
              slides.forEach(function (s, si) {
                var delta = Math.abs(s.offsetLeft - track.offsetLeft - track.scrollLeft);
                if (delta < bestDelta) {
                  bestDelta = delta;
                  best = si;
                }
              });
              paint(best);
            }, 60);
          },
          { passive: true }
        );
      }

      /* Optional zoom: open the active image in the browser's own viewer. */
      var zoom = gallery.querySelector('[data-kps-pdp-gallery-zoom]');
      if (zoom) {
        zoom.addEventListener('click', function () {
          var img = slides[current] && slides[current].querySelector('img');
          if (!img) return;
          var href = img.currentSrc || img.src;
          if (href) window.open(href, '_blank', 'noopener');
        });
      }

      paint(0);
    });
  }

  /* --------------------------------------------------------
     Quantity stepper + stock guard
     -------------------------------------------------------- */
  function initQty(root) {
    each(root, '[data-kps-pdp-qty]', function (wrap) {
      if (!once(wrap, 'kpsQty')) return;

      var input = wrap.querySelector('[data-kps-pdp-qty-input]');
      if (!input) return;
      var limit = parseInt(wrap.getAttribute('data-stock-limit'), 10) || 0;
      var field = wrap.closest('.kps-pdp-mp__qtyfield') || wrap.parentNode;
      var error = field ? field.querySelector('[data-kps-pdp-qty-error]') : null;

      function clampAndReport() {
        var v = parseInt(input.value, 10);
        if (!isFinite(v) || v < 1) v = 1;

        /* limit of 0 means "not tracked / oversell allowed" - no ceiling. */
        var over = limit > 0 && v > limit;
        if (over) v = limit;
        input.value = v;

        if (error) {
          if (over) error.removeAttribute('hidden');
          else error.setAttribute('hidden', '');
        }
      }

      each(wrap, '[data-kps-pdp-qty-step]', function (btn) {
        btn.addEventListener('click', function () {
          var step = parseInt(btn.getAttribute('data-kps-pdp-qty-step'), 10) || 0;
          input.value = (parseInt(input.value, 10) || 1) + step;
          clampAndReport();
        });
      });

      input.addEventListener('change', clampAndReport);
      input.addEventListener('blur', clampAndReport);
    });
  }

  /* --------------------------------------------------------
     Variant price repaint
     The form already posts the right id; this only keeps the displayed price
     and the button price in step.
     -------------------------------------------------------- */
  function initVariantPrice(root) {
    each(root, '[data-kps-pdp-main]', function (section) {
      if (!once(section, 'kpsVariant')) return;

      /* Repaint every price figure from the chosen variant's data-* values.
         Compare-at and unit price are emptied when the variant has none, and
         their :empty CSS rule collapses the slot - otherwise a struck-through
         price from a different variant would stay on screen. */
      function paint(el) {
        if (!el) return;
        var price = el.getAttribute('data-price');
        if (price) {
          each(section, '[data-kps-pdp-price]', function (t) { t.textContent = price; });
        }
        var compare = el.getAttribute('data-compare') || '';
        each(section, '[data-kps-pdp-compare]', function (t) { t.textContent = compare; });
        var unit = el.getAttribute('data-unit') || '';
        each(section, '[data-kps-pdp-unit]', function (t) { t.textContent = unit; });
      }

      each(section, '.kps-pdp-mp__radio', function (radio) {
        radio.addEventListener('change', function () {
          if (radio.checked) paint(radio);
        });
      });

      var select = section.querySelector('[data-kps-pdp-variant-select]');
      if (select) {
        select.addEventListener('change', function () {
          paint(select.options[select.selectedIndex]);
        });
      }
    });
  }

  /* --------------------------------------------------------
     Add to cart feedback
     The theme's own <product-form data-ajax-cart> does the network work and
     adds/removes `.working` on the button. We add the label swap, the cart
     drawer open, and the non-AJAX fallback state.
     -------------------------------------------------------- */
  function openCartDrawer() {
    var drawer = document.getElementById('site-cart-sidebar');
    if (!drawer || typeof drawer.show !== 'function') return false;
    drawer.show();
    var recs = document.getElementById('cart-recommendations-sidebar');
    if (recs && typeof recs.generateRecommendations === 'function') recs.generateRecommendations();
    return true;
  }

  function initAtc(root) {
    /* Main buy button - label swap while the theme is posting. */
    each(root, '[data-kps-pdp-atc]', function (btn) {
      if (!once(btn, 'kpsAtc')) return;

      var label = btn.querySelector('[data-kps-pdp-atc-label]');
      var original = label ? label.textContent : '';
      var adding = btn.getAttribute('data-adding-label') || original;

      var form = btn.closest('form');
      if (form) {
        form.addEventListener('submit', function () {
          btn.classList.add('is-adding');
          if (label && adding) label.textContent = adding;
        });
      }

      var wrap = btn.closest('product-form');
      if (wrap) {
        wrap.addEventListener('add-to-cart', function () {
          btn.classList.remove('is-adding');
          if (label) label.textContent = original;
          openCartDrawer();
        });
      }
    });

    /* Routine cards - "Adding..." then "Added" then back. */
    each(root, '[data-kps-pdp-rail][data-kps-pdp-open-drawer]', function (rail) {
      if (!once(rail, 'kpsCardAtc')) return;
      var wantsDrawer = rail.getAttribute('data-kps-pdp-open-drawer') === 'true';

      each(rail, 'product-form', function (pf) {
        var btn = pf.querySelector('[data-kps-pdp-card-atc]');
        var label = btn ? btn.querySelector('.kps-pdp-rcard__atc-label') : null;
        if (!btn || !label) return;
        var original = label.textContent.trim();
        var resetTimer = null;
        var stuckTimer = null;

        function restore() {
          window.clearTimeout(resetTimer);
          window.clearTimeout(stuckTimer);
          resetTimer = null;
          stuckTimer = null;
          btn.classList.remove('is-working');
          label.textContent = original;
        }

        var form = pf.querySelector('form');
        if (form) {
          form.addEventListener('submit', function () {
            btn.classList.add('is-working');
            label.textContent = 'Adding…';

            /* The theme dispatches add-to-cart from inside the .then() that
               rewrites the cart drawer markup, so anything that throws in
               there - a missing #AjaxCartForm, say - skips straight to its
               .catch and the event never arrives. The theme's own .working
               class survives that because it is cleared in a .finally; this
               label would not, and the button would read "Adding…" forever.
               So: an outside time limit, cancelled the moment the event does
               land. Never mind the drawer in that case - just give the button
               back. */
            window.clearTimeout(stuckTimer);
            stuckTimer = window.setTimeout(restore, 8000);
          });
        }

        pf.addEventListener('add-to-cart', function () {
          window.clearTimeout(stuckTimer);
          stuckTimer = null;
          label.textContent = 'Added';
          window.clearTimeout(resetTimer);
          resetTimer = window.setTimeout(restore, 1500);
          if (wantsDrawer) openCartDrawer();
        });
      });
    });
  }

  /* --------------------------------------------------------
     Sticky mobile ATC bar
     Revealed by IntersectionObserver on a sentinel, never by a scroll handler.
     -------------------------------------------------------- */
  function initStickyBar(root) {
    each(root, '[data-kps-pdp-sticky-bar]', function (bar) {
      if (!once(bar, 'kpsSticky')) return;

      var section = bar.closest('[data-kps-pdp-main]');
      if (!section) return;
      var mainBtn = section.querySelector('[data-kps-pdp-atc]');
      var sentinel = section.querySelector('[data-kps-pdp-atc-sentinel]');
      var proxy = bar.querySelector('[data-kps-pdp-sticky-submit]');

      /* The bar has no form of its own: it forwards to the real buy box, so
         there is one source of truth for variant and quantity. */
      if (proxy && mainBtn) {
        proxy.addEventListener('click', function () {
          mainBtn.click();
        });
      }

      var watch = mainBtn || sentinel;
      if (!watch || !('IntersectionObserver' in window)) return;

      /* `seen` gates the reveal on the main button having actually been in
         view at least once. Without it the bar would be showing before the
         visitor has scrolled at all - the button starts below the fold on a
         phone, so "not intersecting" is true from the first frame. */
      var seen = false;

      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              seen = true;
              bar.setAttribute('hidden', '');
            } else if (seen) {
              /* Only ever visible below 990px - CSS enforces that too. */
              bar.removeAttribute('hidden');
            }
          });
        },
        { rootMargin: '0px', threshold: 0 }
      ).observe(watch);
    });
  }

  /* --------------------------------------------------------
     Sticky info column height guard
     If the buy box is taller than the viewport, sticky traps its bottom
     off-screen - so sticky is switched off in that case.
     -------------------------------------------------------- */
  /* Stands sticky down when the column cannot fit on screen. This only makes
     sense for the INFO column: pinning a buy box taller than the viewport
     leaves its bottom - Add to Cart included - permanently out of reach, so
     falling back to normal scrolling is the lesser evil.

     It is wrong for the gallery. A gallery is not something you have to reach
     the bottom of, and standing sticky down there just means the setting
     silently does nothing on any laptop where the images happen to be taller
     than the window - which is what the guard was doing when sticky moved to
     the gallery. The reference sticks its gallery with no such test at all. */
  function checkStickyHeight(col) {
    if (!col) return;
    if (col.getAttribute('data-kps-pdp-sticky') !== 'info') {
      col.classList.remove('is-too-tall');
      return;
    }
    var tooTall = col.scrollHeight > window.innerHeight - 40;
    col.classList.toggle('is-too-tall', tooTall);
  }

  function initSticky(root) {
    /* Measures whichever column the section made sticky, which is not always
       the info column - the default is the gallery, matching the reference. The
       attribute is emitted on one column or neither, so this also no-ops
       cleanly when sticky is switched off entirely. */
    each(root, '[data-kps-pdp-sticky]', function (col) {
      checkStickyHeight(col);
      if (!once(col, 'kpsStickyCol')) return;
      /* The column grows when an accordion opens, so re-measure on toggle.
         Only relevant when the info column is the sticky one, but harmless
         otherwise: a media column has no <details> to find. */
      each(col, 'details', function (d) {
        d.addEventListener('toggle', function () { checkStickyHeight(col); });
      });
    });
  }

  /* --------------------------------------------------------
     Modal (<dialog>)
     The browser supplies the backdrop, ESC and the top layer. We add the
     scroll lock and focus return.
     -------------------------------------------------------- */
  function initModals(root) {
    each(root, '[data-kps-pdp-modal-open]', function (trigger) {
      if (!once(trigger, 'kpsModalOpen')) return;

      trigger.addEventListener('click', function () {
        var id = trigger.getAttribute('data-kps-pdp-modal-open');
        var dialog = document.getElementById(id);
        if (!dialog) return;

        dialog.__kpsReturnFocus = trigger;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        document.documentElement.style.overflow = 'hidden';

        /* App-injected floaters (Yotpo Loyalty's "sign up" bar, chat bubbles)
           are position:fixed with their own stacking, and they paint over the
           drawer. Flagging the root lets CSS stand them down for as long as
           the drawer is open, without touching how they behave anywhere else
           on the page. */
        if (dialog.hasAttribute('data-kps-pdp-drawer')) {
          document.documentElement.classList.add('kps-pdp-drawer-open');
        }
      });
    });

    each(root, '[data-kps-pdp-modal]', function (dialog) {
      if (!once(dialog, 'kpsModal')) return;

      function hardClose() {
        dialog.classList.remove('is-closing');
        if (typeof dialog.close === 'function' && dialog.open) dialog.close();
        else dialog.removeAttribute('open');
      }

      /* A <dialog> is display:none the moment it closes, so there is no way to
         transition it out - the close has to be held back for the length of
         one animation. Only drawers do this, and only when motion is welcome;
         a centred modal still closes instantly. */
      function wantsSlideOut() {
        if (!dialog.hasAttribute('data-kps-pdp-drawer')) return false;
        if (!window.matchMedia) return false;
        return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      function close() {
        if (!dialog.open) return;
        if (!wantsSlideOut()) { hardClose(); return; }
        if (dialog.classList.contains('is-closing')) return;

        dialog.classList.add('is-closing');

        /* Belt and braces: if animationend never arrives - animation stripped
           by an override, tab backgrounded mid-flight - the timer still closes
           it. A drawer that cannot be dismissed is far worse than one that
           closes without its slide. */
        var done = false;
        function finish() {
          if (done) return;
          done = true;
          dialog.removeEventListener('animationend', onEnd);
          hardClose();
        }
        function onEnd(e) {
          if (e.target !== dialog) return;
          finish();
        }
        dialog.addEventListener('animationend', onEnd);
        setTimeout(finish, 500);
      }

      each(dialog, '[data-kps-pdp-modal-close]', function (btn) {
        btn.addEventListener('click', close);
      });

      /* ESC closes a dialog natively and instantly, which would skip the
         slide-out. Cancel the native close and run ours instead. */
      dialog.addEventListener('cancel', function (e) {
        if (!wantsSlideOut()) return;
        e.preventDefault();
        close();
      });

      /* Click on the backdrop. The dialog element covers only the panel, so a
         click whose target IS the dialog landed outside the panel. */
      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) close();
      });

      dialog.addEventListener('close', function () {
        document.documentElement.style.overflow = '';
        /* Cleared on the native close event, so it lifts however the drawer
           was dismissed - button, backdrop, ESC, or the timeout fallback. */
        document.documentElement.classList.remove('kps-pdp-drawer-open');
        var back = dialog.__kpsReturnFocus;
        if (back && typeof back.focus === 'function') back.focus();
      });
    });
  }

  /* --------------------------------------------------------
     Inline videos (video-reviews section)
     -------------------------------------------------------- */
  function allVideos() {
    return Array.prototype.slice.call(document.querySelectorAll('.kps-pdp-vr__video'));
  }

  function initVideos(root) {
    each(root, '[data-kps-pdp-video-frame]', function (frame) {
      if (!once(frame, 'kpsVideo')) return;

      var video = frame.querySelector('video');
      if (!video) return;

      var sound = frame.querySelector('[data-kps-pdp-video-sound]');
      var play = frame.querySelector('[data-kps-pdp-video-play]');

      /* Autoplay is allowed only on a wide-enough viewport, with motion
         allowed and no data-saver signal (spec 12 / 1.5.9). */
      var mqAutoplay = window.matchMedia('(min-width: 750px)');
      function autoplayAllowed() {
        return mqAutoplay.matches && !prefersReduced() && !saveData();
      }

      function start() {
        video.muted = video.muted !== false;
        var r = video.play();
        if (r && typeof r.catch === 'function') {
          /* A blocked play() is expected on some browsers - keep the poster
             and the play button rather than throwing. */
          r.catch(function () { frame.classList.remove('is-playing'); });
        }
      }

      video.addEventListener('playing', function () { frame.classList.add('is-playing'); });
      video.addEventListener('pause', function () { frame.classList.remove('is-playing'); });

      if (play) {
        play.addEventListener('click', function () {
          if (video.preload === 'none') video.preload = 'auto';
          start();
        });
      }

      if (sound) {
        sound.addEventListener('click', function () {
          var unmuting = video.muted;
          if (unmuting) {
            /* Only one video may have sound: mute and pause the others. */
            allVideos().forEach(function (other) {
              if (other === video) return;
              other.muted = true;
              other.pause();
              var otherBtn =
                other.closest('[data-kps-pdp-video-frame]') &&
                other.closest('[data-kps-pdp-video-frame]').querySelector('[data-kps-pdp-video-sound]');
              if (otherBtn) otherBtn.setAttribute('aria-pressed', 'false');
            });
          }
          video.muted = !unmuting;
          sound.setAttribute('aria-pressed', unmuting ? 'true' : 'false');
          if (unmuting) start();
        });
      }

      if (!('IntersectionObserver' in window)) return;

      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              if (!autoplayAllowed()) return;
              if (video.preload === 'none') video.preload = 'auto';
              start();
            } else {
              /* Off-screen video costs CPU and battery for nothing. */
              video.pause();
            }
          });
        },
        { threshold: 0.5 }
      ).observe(frame);
    });
  }

  /* --------------------------------------------------------
     UGC wall - endless centred rail, live tile in the middle
     -------------------------------------------------------- */
  function initUgcWall(root) {
    each(root, '[data-kps-pdp-ugc]', function (wall) {
      if (!once(wall, 'kpsUgc')) return;

      var rail = wall.querySelector('[data-kps-pdp-rail]');
      if (!rail) return;

      /* The tiles the section rendered. Everything below may add copies of
         them, so this list stays the record of what is real. */
      var real = railItems(rail);
      var n = real.length;
      if (!n) return;

      /* Turns on the end spacers that let the first and last tile reach the
         middle. Set here rather than in the markup because without a script
         nothing can scroll the wall off zero, and the leading spacer would
         then just be half a screen of nothing. */
      rail.classList.add('is-centred');

      /* Distance from one tile to the next, measured rather than computed: the
         tile width is a percentage of the rail and the gap is fluid. */
      function step() {
        var items = railItems(rail);
        if (items.length > 1) return Math.abs(items[1].offsetLeft - items[0].offsetLeft);
        return items.length ? items[0].offsetWidth : 0;
      }

      /* ---- Endless wall ----
         Whichever tile is centred, tiles have to run off both edges of the
         screen - the wall has no visible first or last tile. Repeating the set
         in the DOM and shifting the scroll position back by whole sets once the
         centred tile leaves the middle copy gives exactly that, and leaves the
         native swipe, the snap points, the arrows and the keyboard order
         working as they are. The copies reuse poster URLs already in the
         cache, so an endless wall costs no extra bytes.

         Copies per side: enough sets to keep both edges of the viewport
         covered while the centred tile is anywhere in the middle copy, i.e.
         copies * setWidth >= (viewport + tile) / 2. Measured once against the
         wider of this rail and the window - a tile is close to the same width
         at every breakpoint, because the tiles-per-view count rises with the
         screen, so a resize never needs a rebuild. */
      var homeStart = 0;

      function cloneSet() {
        var frag = document.createDocumentFragment();
        real.forEach(function (cell, i) {
          var copy = cell.cloneNode(true);
          copy.classList.add('is-clone');
          copy.classList.remove('is-active');
          copy.setAttribute('aria-hidden', 'true');
          /* Two elements answering to one editor block id would confuse the
             theme editor's block selection. */
          copy.removeAttribute('data-shopify-editor-block');
          /* A copy is not a lightbox entry of its own - it forwards to the
             real tile, so the lightbox keeps one story per video. */
          each(copy, '[data-kps-pdp-lightbox-open]', function (btn) {
            btn.removeAttribute('data-kps-pdp-lightbox-open');
            btn.setAttribute('data-kps-pdp-ugc-echo', String(i));
            btn.setAttribute('tabindex', '-1');
          });
          frag.appendChild(copy);
        });
        return frag;
      }

      function buildLoop() {
        var one = step();
        if (!one) return;
        var set = one * n;
        var view = Math.max(rail.clientWidth, window.innerWidth || 0, 1280);
        var copies = Math.max(1, Math.ceil((view + one) / (2 * set)));

        for (var a = 0; a < copies; a++) rail.insertBefore(cloneSet(), real[0]);
        for (var b = 0; b < copies; b++) rail.appendChild(cloneSet());

        homeStart = copies * n;
        /* Open on the first real tile - the one the markup already highlights
           and the one the first dot stands for. */
        railScrollTo(rail, homeStart, true);
      }

      /* Back to the middle copy, by whole sets. Tile positions repeat with
         that period, so the shift itself is invisible: nothing moves on screen.
         What would give it away is the highlight, which lands on a different
         element - the copy of the same video - and would animate in from the
         veiled state. So the highlight is moved with transitions off and a
         reflow forced in between, which makes the new state the starting point
         rather than something to animate towards. */
      function normalize() {
        if (!homeStart) return;
        var d = Math.floor((railIndex(rail) - homeStart) / n);
        if (d === 0) return;
        var one = step();
        if (!one) return;

        rail.classList.add('is-shifting');
        rail.scrollLeft = rail.scrollLeft - d * n * one;
        updateRailNav(rail);
        void rail.offsetHeight;
        rail.classList.remove('is-shifting');
      }

      if (wall.getAttribute('data-loop') === 'true' && n > 1) buildLoop();

      /* Only once the scroll has settled: shifting mid-swipe would cancel the
         momentum on iOS. */
      var idle = null;
      rail.addEventListener(
        'scroll',
        function () {
          window.clearTimeout(idle);
          idle = window.setTimeout(normalize, 160);
        },
        { passive: true }
      );

      /* One delegated handler for every copy on the rail. */
      rail.addEventListener('click', function (e) {
        var echo = e.target.closest ? e.target.closest('[data-kps-pdp-ugc-echo]') : null;
        if (!echo) return;
        var cell = real[parseInt(echo.getAttribute('data-kps-pdp-ugc-echo'), 10)];
        var btn = cell && cell.querySelector('[data-kps-pdp-lightbox-open]');
        if (btn) btn.click();
      });

      /* ---- Live centre tile ----
         The centred tile plays its own video, muted and looping, over its
         poster; every other tile stays a poster behind the veil. One <video>
         element moves between tiles, so the wall never has more than one video
         decoding however long it is. */
      var wantsLive = wall.getAttribute('data-live') === 'true';
      var video = null;
      var onScreen = true;

      function stopLive() {
        if (!video) return;
        video.pause();
        video.classList.remove('is-ready');
        if (video.parentNode) video.parentNode.removeChild(video);
      }

      function showLive() {
        if (!wantsLive || !onScreen || prefersReduced() || saveData()) {
          stopLive();
          return;
        }
        var cell = railItems(rail)[railIndex(rail)];
        var tile = cell && cell.querySelector('[data-src]');
        var frame = cell && cell.querySelector('[data-kps-pdp-ugc-frame]');
        var src = tile && tile.getAttribute('data-src');
        if (!frame || !src) {
          stopLive();
          return;
        }

        if (!video) {
          video = document.createElement('video');
          video.className = 'kps-pdp-ugc__live';
          video.muted = true;
          video.defaultMuted = true;
          video.loop = true;
          video.playsInline = true;
          video.setAttribute('muted', '');
          video.setAttribute('loop', '');
          video.setAttribute('playsinline', '');
          video.setAttribute('preload', 'none');
          video.setAttribute('aria-hidden', 'true');
          video.tabIndex = -1;
          /* Held back until there are real frames, so a poster is never
             replaced by a black box. */
          video.addEventListener('playing', function () { video.classList.add('is-ready'); });
        }
        /* Re-parenting inside the same task never pauses a media element, so
           a tile-to-tile move is seamless and must not fade. */
        if (video.parentNode !== frame) frame.appendChild(video);
        if (video.getAttribute('src') !== src) {
          video.classList.remove('is-ready');
          video.setAttribute('src', src);
        }
        var r = video.play();
        if (r && typeof r.catch === 'function') r.catch(function () {});
      }

      rail.addEventListener('kps:rail:change', showLive);

      /* The lightbox owns the sound, so the muted tile steps aside for it. */
      var dialog = wall.querySelector('[data-kps-pdp-lightbox]');
      if (dialog) {
        rail.addEventListener('click', function (e) {
          if (e.target.closest && e.target.closest('[data-kps-pdp-ugc-tile]')) stopLive();
        });
        dialog.addEventListener('close', showLive);
      }

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              onScreen = entry.isIntersecting;
              if (onScreen) showLive();
              else stopLive();
            });
          },
          { threshold: 0.2 }
        ).observe(rail);
      } else {
        showLive();
      }
    });
  }

  /* --------------------------------------------------------
     UGC story lightbox
     -------------------------------------------------------- */
  function initLightbox(root) {
    each(root, '[data-kps-pdp-lightbox-group]', function (group) {
      if (!once(group, 'kpsLb')) return;
      if (group.getAttribute('data-kps-pdp-lightbox-group') !== 'true') return;

      var dialog = group.querySelector('[data-kps-pdp-lightbox]');
      if (!dialog) return;

      var video = dialog.querySelector('[data-kps-pdp-lb-video]');
      var caption = dialog.querySelector('[data-kps-pdp-lb-caption]');
      var bars = Array.prototype.slice.call(dialog.querySelectorAll('.kps-pdp-lb__bar'));
      var triggers = Array.prototype.slice.call(group.querySelectorAll('[data-kps-pdp-lightbox-open]'));
      var current = 0;
      var returnFocus = null;

      function load(i) {
        if (!triggers.length) return;
        current = (i + triggers.length) % triggers.length;
        var t = triggers[current];
        var src = t.getAttribute('data-src');

        if (video) {
          video.pause();
          if (src) {
            video.src = src;
            video.currentTime = 0;
            var r = video.play();
            if (r && typeof r.catch === 'function') r.catch(function () {});
          } else {
            video.removeAttribute('src');
          }
        }
        if (caption) caption.textContent = t.getAttribute('data-caption') || '';
        bars.forEach(function (b, bi) { b.classList.toggle('is-done', bi <= current); });
      }

      function open(i, trigger) {
        returnFocus = trigger || null;
        load(i);
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        document.documentElement.style.overflow = 'hidden';
      }

      function close() {
        if (video) {
          video.pause();
          video.removeAttribute('src');
          video.load();
        }
        if (typeof dialog.close === 'function' && dialog.open) dialog.close();
        else dialog.removeAttribute('open');
      }

      triggers.forEach(function (t, ti) {
        t.addEventListener('click', function () {
          if (t.disabled) return;
          open(ti, t);
        });
      });

      var closeBtn = dialog.querySelector('[data-kps-pdp-lb-close]');
      var prevBtn = dialog.querySelector('[data-kps-pdp-lb-prev]');
      var nextBtn = dialog.querySelector('[data-kps-pdp-lb-next]');
      if (closeBtn) closeBtn.addEventListener('click', close);
      if (prevBtn) prevBtn.addEventListener('click', function () { load(current - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { load(current + 1); });

      /* Auto-advance to the next story when one finishes. */
      if (video) video.addEventListener('ended', function () { load(current + 1); });

      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) close();
      });

      dialog.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); load(current + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); load(current - 1); }
      });

      dialog.addEventListener('close', function () {
        document.documentElement.style.overflow = '';
        if (returnFocus && typeof returnFocus.focus === 'function') returnFocus.focus();
      });
    });
  }

  /* --------------------------------------------------------
     Returns bar (Redo)
     -------------------------------------------------------- */
  /* The app builds its "Free Unlimited Returns" bar in JavaScript and mounts
     it near the top of the product form. It cannot be positioned with CSS,
     because the element is created by a lazily loaded chunk of the app - there
     is no name the section could put in a stylesheet, and `order` only applies
     to a direct flex child in any case. So the section renders an empty slot
     below the Buy with Shop block and we move the app's element into it, which
     is where the bar sits on the live theme.

     The slot is inside the product form, so any hidden input the app added
     stays form-associated after the move.

     Matching is on the word "redo" in the tag, id or class rather than on one
     exact element name: a rename inside the app should not silently put the
     bar back above the buttons. */
  var RETURNS_WORD = /(^|[^a-z])redo([^a-z]|$)/i;

  function isReturnsEl(el, slot) {
    if (!el || el.nodeType !== 1) return false;
    if (el === slot || slot.contains(el)) return false;
    var tag = el.tagName.toLowerCase();
    if (tag.indexOf('redo-') === 0 || tag === 'x-redo') return true;
    if (el.id && RETURNS_WORD.test(el.id)) return true;
    var cls = typeof el.className === 'string' ? el.className : '';
    return !!(cls && RETURNS_WORD.test(cls));
  }

  function moveReturnsInto(slot, scope) {
    var moved = 0;
    Array.prototype.forEach.call(scope.querySelectorAll('*'), function (el) {
      if (!isReturnsEl(el, slot)) return;
      /* Only the outermost match moves. The app nests its own children, and
         relocating those one by one would take the bar apart. */
      var p = el.parentElement;
      while (p && p !== scope) {
        if (isReturnsEl(p, slot)) return;
        p = p.parentElement;
      }
      slot.appendChild(el);
      moved++;
    });
    return moved;
  }

  function initReturnsBar(root) {
    each(root, '[data-kps-pdp-returns-slot]', function (slot) {
      if (!once(slot, 'kpsReturns')) return;
      var scope = (slot.closest && slot.closest('.kps-pdp-mp__buybox')) || slot.parentElement;
      if (!scope) return;

      moveReturnsInto(slot, scope);
      if (!window.MutationObserver) return;

      /* The bar can mount late and can be re-rendered on a variant change, so
         the observer stays live rather than running once. It is capped: an app
         that re-parents its own element would otherwise ping-pong with us for
         the life of the page. */
      var budget = 20;
      var queued = false;
      var mo = new MutationObserver(function () {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
          queued = false;
          if (budget <= 0) {
            mo.disconnect();
            return;
          }
          if (moveReturnsInto(slot, scope) > 0) budget--;
        });
      });
      mo.observe(scope, { childList: true, subtree: true });
    });
  }

  /* --------------------------------------------------------
     Boot
     -------------------------------------------------------- */
  function initAll(root) {
    root = root || document;
    measureHeader();
    initReveal(root);
    initAnchors(root);
    /* Before initRail: the wall adds its looping copies to the rail, and it
       wants the rail's first change event. */
    initUgcWall(root);
    each(root, '[data-kps-pdp-rail]', initRail);
    initTestimonials(root);
    initAccordions(root);
    initFaqTabs(root);
    initCountUp(root);
    initBeforeAfter(root);
    initSync(root);
    initGallery(root);
    initQty(root);
    initVariantPrice(root);
    initAtc(root);
    initReturnsBar(root);
    initStickyBar(root);
    initSticky(root);
    initModals(root);
    initVideos(root);
    initLightbox(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('orientationchange', onResize);
  /* Late web fonts change the header height and the info-column height. */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { onResize(); });
  }

  /* Theme editor: a re-rendered section needs its behaviour bound again. */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target || document);
  });
  document.addEventListener('shopify:section:unload', function () {
    document.documentElement.style.overflow = '';
  });
  document.addEventListener('shopify:block:select', function (e) {
    var block = e.target;
    if (!block) return;

    /* Selecting a card in the editor should bring it into the rail and, where
       the rail highlights its current card, make it the highlighted one. */
    var rail = block.parentNode && block.parentNode.hasAttribute && block.parentNode.hasAttribute('data-kps-pdp-rail')
      ? block.parentNode
      : null;
    if (rail) {
      var at = railItems(rail).indexOf(block);
      /* Only scroll when the card is not already on screen. -1 would clamp to
         0, and scrolling a card that is already visible makes the editor jump
         the rail (to its end, for a trailing block) every time a block is
         selected - including the auto-selection on load. */
      var railBox = rail.getBoundingClientRect();
      var cardBox = block.getBoundingClientRect();
      var visible = cardBox.left >= railBox.left - 1 && cardBox.right <= railBox.right + 1;
      if (at > -1 && !visible) railScrollTo(rail, at);
    }

    /* Selecting a testimonial or slide block in the editor should reveal it. */
    var slide = block.closest && block.closest('[data-kps-pdp-ts-slide]');
    if (slide) {
      var wrap = slide.closest('[data-kps-pdp-ts]');
      if (wrap) {
        each(wrap, '[data-kps-pdp-ts-slide]', function (s) { s.classList.remove('is-active'); });
        slide.classList.add('is-active');
      }
    }
  });
})();
